import type { DependencyList, EffectCallback } from 'react';
import type { Func } from '@ricoh/ria.client.core/types/core/function.type';

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { doNothing } from '@ricoh/ria.client.core/constants/core/action';
import { newUUID } from '@ricoh/ria.client.core/utils/data/key';

/**
 * Call hook `useEffect` that is just at first rendering.
 *
 * @param {EffectCallback} effect Effect callback.
 */
export function useFirstRenderEffect(effect: EffectCallback) {
  // * Use empty DependencyList for first rendering effect.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(effect, []);
}

/**
 * Call hook `useLayoutEffect` that is just at first rendering.
 *
 * @param {EffectCallback} effect Effect callback.
 */
export function useFirstRenderLayoutEffect(effect: EffectCallback) {
  // * Use empty DependencyList for first rendering layout effect.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(effect, []);
}

/**
 * Use `isFirstRender` to conditionally execute code only on the initial render.
 *
 * @returns {boolean} True if the component is rendering for the first time, otherwise false.
 */
export function useIsFirstRender(): boolean {
  const isFirst = useRef<boolean>(true);

  if (isFirst.current) {
    isFirst.current = false;

    return true;
  }

  return isFirst.current;
}

/**
 * Call hook `useEffect` that is skipping the first render.
 *
 * @param {EffectCallback} effect Effect callback.
 * @param {DependencyList} deps Dependencies.
 */
export function useUpdateEffect(effect: EffectCallback, deps?: DependencyList) {
  const isFirst = useIsFirstRender();

  useEffect(() => {
    if (!isFirst) {
      return effect();
    }

    return doNothing;

    // * Use `deps` params as DependencyList
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/**
 * Call hook `useLayoutEffect` that is skipping the first render.
 *
 * @param {EffectCallback} effect Effect callback.
 * @param {DependencyList} deps Dependencies.
 */
export function useUpdateLayoutEffect(effect: EffectCallback, deps?: DependencyList) {
  const isFirst = useIsFirstRender();

  useLayoutEffect(() => {
    if (!isFirst) {
      return effect();
    }

    return doNothing;

    // * Use `deps` params as DependencyList
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/**
 * Force re-render.
 *
 * @returns {[forceUpdate: Func, state: number]} Function to force re-render.
 */
export function useForceUpdate(): [forceUpdate: Func, state: string] {
  const [state, setState] = useState<string>(newUUID(true));

  const forceUpdate = useCallback<Func>(() => {
    setState(() => newUUID(true));
  }, []);

  return [forceUpdate, state];
}
